import { inject, Injectable } from '@angular/core';
import { TypeAPI } from '../../api/type-api';
import { map, shareReplay, tap } from 'rxjs/operators';
import { PhoneDropdownItem } from '../../models/shared/phone-dropdown-item';
import { BehaviorSubject, Observable } from 'rxjs';
import { CANADA_COUNTRY_ID, USA_COUNTRY_ID } from '../../consts/consts';
import { GeographyService } from '../geography/geography.service';

@Injectable({
  providedIn: 'root'
})
export class TypeService {
  private typeAPI = inject(TypeAPI);
  private geographyService = inject(GeographyService);

  /** @deprecated Inject {@link GeographyService} and use `statesForCountry(USA_COUNTRY_ID)`. */
  public states$ = this.geographyService.statesForCountry(USA_COUNTRY_ID);

  /** @deprecated Inject {@link GeographyService} and use `statesForCountry(CANADA_COUNTRY_ID)`. */
  public provinces$ = this.geographyService.statesForCountry(CANADA_COUNTRY_ID);

  // Genders
  public genders$ = this.typeAPI.getGenders().pipe(
    tap(() => this._gendersAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _gendersAreLoaded = new BehaviorSubject(false);
  public gendersAreLoaded$ = this._gendersAreLoaded as Observable<boolean>;

  // Pronouns
  public pronouns$ = this.typeAPI.getPronouns().pipe(
    tap(() => this._pronounsAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _pronounsAreLoaded = new BehaviorSubject(false);
  public pronounsAreLoaded$ = this._pronounsAreLoaded as Observable<boolean>;

  /** @deprecated Inject {@link GeographyService} and use `countries$`. */
  public countries$ = this.geographyService.countries$.pipe(tap(() => this._countriesAreLoaded.next(true)));

  private _countriesAreLoaded = new BehaviorSubject(false);
  public countriesAreLoaded$ = this._countriesAreLoaded as Observable<boolean>;

  // Country Codes
  public countryCodes$ = this.countries$.pipe(
    map(countries => countries.map(c => new PhoneDropdownItem(c))),
    tap(() => this._countryCodesAreLoaded.next(true)),
    shareReplay()
  );
  private _countryCodesAreLoaded = new BehaviorSubject(false);
  public countryCodesAreLoaded$ = this._countryCodesAreLoaded as Observable<boolean>;

  // Submission Statuses
  public submissionStatuses$ = this.typeAPI
    .getSubmissionStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Alert Types
  public alertTypes$ = this.typeAPI.getAlertTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // All contribution entry code types that are valid on a remittance
  public remittanceEntryCodeTypes$ = this.typeAPI
    .getContributionEntryCodeTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Contribution Entry Option Types
  public contributionEntryOptionTypes$ = this.remittanceEntryCodeTypes$.pipe(
    map(types =>
      types.filter(type => {
        return [201, 202, 205, 206].includes(type.id);
      })
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Transaction Entry Code Types
  public transactionEntryCodeTypes$ = this.typeAPI.getTransactionEntryCodeTypes().pipe(shareReplay(1));

  // Transaction Status Types
  public transactionStatusTypes$ = this.typeAPI
    .getTransactionStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Remittance Status Types
  public remittanceStatusTypes$ = this.typeAPI
    .getRemittanceStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Remittance RemittancePayment Status Types
  public remittancePaymentStatusTypes$ = this.typeAPI
    .getRemittancePaymentStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Payment Method Types
  public paymentMethodTypes$ = this.typeAPI
    .getPaymentMethodTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Multi-Factor Authentication Types
  public mfaTypes$ = this.typeAPI.getMfaTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Member Employment Status Types
  public memberEmploymentStatusTypes$ = this.typeAPI
    .getMemberEmploymentStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Member Employment Status Change Reason Types
  public memberEmploymentStatusChangeReasonTypes$ = this.typeAPI
    .getMemberEmploymentStatusChangeReasonTypes()
    .pipe(shareReplay());

  // Scheduled Remittance Frequency Types
  public scheduledRemittanceFrequencyTypes$ = this.typeAPI
    .getScheduledRemittanceFrequencyTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Scheduled Remittance Status Types
  public scheduledRemittanceStatusTypes$ = this.typeAPI
    .getScheduledRemittanceStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Fund Types
  public fundTypes$ = this.typeAPI.getFundTypes().pipe(
    tap(() => this._fundTypesAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _fundTypesAreLoaded = new BehaviorSubject(false);
  public fundTypesAreLoaded$ = this._fundTypesAreLoaded as Observable<boolean>;

  // Accounts
  public accountTypes$ = this.typeAPI.getAccountTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public accountStatusTypes$ = this.typeAPI
    .getAccountStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Member Relationship Types
  public memberRelationshipTypes$ = this.typeAPI.getMemberRelationshipTypes().pipe(
    tap(() => this._memberRelationshipTypesAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Beneficiary Relationship to Member Types
  public beneficiaryRelationshipToMemberTypes$ = this.typeAPI.getBeneficiaryRelationshipToMemberTypes().pipe(
    tap(() => this._relationshipToMemberTypesAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _relationshipToMemberTypesAreLoaded = new BehaviorSubject(false);
  public relationshipToMemberTypesAreLoaded$ = this._relationshipToMemberTypesAreLoaded as Observable<boolean>;

  private _memberRelationshipTypesAreLoaded = new BehaviorSubject(false);
  public memberRelationshipTypesAreLoaded$ = this._memberRelationshipTypesAreLoaded as Observable<boolean>;

  // Fiduciary Types

  public fiduciaryTypes$ = this.typeAPI.getFiduciaryTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fiduciaryEntityTypes$ = this.typeAPI
    .getFiduciaryEntityTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fiduciaryPowerOfAttorneyTypes$ = this.typeAPI
    .getFiduciaryPowerOfAttorneyTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fiduciaryPowerOfAttorneyAppoinmentTypes$ = this.typeAPI
    .getFiduciaryPowerOfAttorneyAppointmentTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Employer Status Types

  public employerStatusTypes$ = this.typeAPI
    .getEmployerStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Member Status Types

  public memberStatusTypes$ = this.typeAPI.getMemberStatusTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Waiver Types
  public consentWaiverTypes$ = this.typeAPI.getConsentWaiverTypes().pipe(
    tap(() => this._waiverTypesLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _waiverTypesLoaded = new BehaviorSubject(false);
  public waiverTypesLoaded$ = this._waiverTypesLoaded.asObservable();

  // Retirement Types
  public retirementTypes$ = this.typeAPI.getRetirementTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Life Status Types
  public lifeStatusTypes$ = this.typeAPI.getLifeStatusTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Death Record Contact Method Types
  public deathRecordContactMethodTypes$ = this.typeAPI
    .getDeathRecordContactMethodTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Death Record Settlement Types
  public deathRecordSettlementTypes$ = this.typeAPI
    .getDeathRecordSettlementTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Transfer-In Source Account Types
  public transferInSourceAccountTypes$ = this.typeAPI.getTransferInSourceAccountTypes().pipe(shareReplay());
}
